import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Phone, User, CheckCircle2, Briefcase, Zap, Loader2 } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function Hero({
  introComplete = true
}: {
  introComplete?: boolean;
}) {
  const {
    t
  } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    caseType: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.caseType) return;
    setSubmitting(true);
    // Simulate submission
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
    }, 800);
  };
  const isValid = formData.name.trim().length > 1 && formData.phone.trim().length >= 7 && formData.caseType !== '';
  return <section id="hero" className="relative w-full min-h-screen flex items-center overflow-hidden bg-black" data-id="element-76">
      <div className="absolute inset-0 hero-bg z-0" data-id="element-77"></div>
      <div className="absolute inset-0 opacity-[0.02] hero-marble animate-marble-shift z-0" data-id="element-78"></div>

      {/* FULL-WIDTH HERO IMAGE BACKGROUND WITH CINEMATIC PAN */}
      <motion.div initial={{
      opacity: 0,
      scale: 1.05
    }} animate={introComplete ? {
      opacity: 1,
      scale: 1
    } : {
      opacity: 0,
      scale: 1.05
    }} transition={{
      duration: 2.4,
      ease: [0.16, 1, 0.3, 1],
      delay: introComplete ? 0.1 : 0
    }} className="absolute inset-0 z-[1] overflow-hidden" data-id="element-79">
        <div className="w-full h-full animate-cinematic-pan origin-center" data-id="element-80">
          <img src="https://cdn.magicpatterns.com/uploads/isAb5vi5PRjhaSrXbSM5bG/ChatGPT_Image_9_may_2026,_13_50_28.png" alt="Elias Navarro Law - Elite Representation" className="w-full h-full object-cover object-center" data-id="element-81" />
        </div>
        {/* Brighter cinematic gradients — image shines through, minimal darkening for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/15 to-transparent lg:from-black/55 lg:via-transparent lg:to-transparent" data-id="element-82"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" data-id="element-83"></div>
      </motion.div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-8 lg:px-[60px] grid grid-cols-1 lg:grid-cols-12 items-center gap-10 min-h-screen" data-id="element-84">
        {/* LEFT: Content */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={introComplete ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 40
      }} transition={{
        duration: 1.6,
        ease: [0.16, 1, 0.3, 1],
        delay: introComplete ? 0.6 : 0
      }} className="pt-32 lg:pt-20 pb-20 lg:pb-0 lg:col-span-8 xl:col-span-7" data-id="element-85">
          <div className="inline-flex items-center gap-3 bg-white/[0.03] border border-white/[0.05] px-6 py-2.5 rounded-full mb-12 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]" data-id="element-86">
            <span className="w-1.5 h-1.5 bg-crimson-glow rounded-full animate-dot-pulse" data-id="element-87"></span>
          </div>

          <div className="font-sans text-[10px] tracking-[8px] uppercase text-gold-light font-medium mb-8 flex items-center gap-5 before:content-[''] before:w-12 before:h-px before:bg-gradient-to-r before:from-gold before:to-transparent opacity-90" data-id="element-88">
            {t('hero.tagline')}
          </div>

          <h1 className="font-serif text-[42px] sm:text-[56px] lg:text-[72px] xl:text-[84px] font-medium leading-[1.05] text-white mb-10 tracking-[-0.02em] [text-shadow:0_10px_40px_rgba(0,0,0,0.8)]" data-id="element-89">
            {t('hero.headline.1')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white/90 to-white/60 block mt-2" data-id="element-90">
              {t('hero.headline.2')}
            </span>
            <span className="block mt-2" data-id="element-91">{t('hero.headline.3')}</span>
          </h1>

          <p className="font-sans text-[15px] lg:text-[17px] leading-[1.9] text-white/60 font-light tracking-[0.02em] max-w-[540px] mb-10 border-l border-crimson/50 pl-6" data-id="element-92">
            {t('hero.subheadline')}
          </p>

          {/* Lead Capture Form — Glass Card */}
          <div className="max-w-[560px]" data-id="element-93">
            <AnimatePresence mode="wait" data-id="element-94">
              {!submitted ? <motion.form key="form" onSubmit={handleSubmit} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.5
            }} className="relative bg-black/40 backdrop-blur-2xl border border-white/[0.08] rounded-sm p-6 lg:p-7 shadow-[0_30px_80px_rgba(0,0,0,0.6)]" data-id="element-95">
                  <div className="absolute top-0 left-0 h-[2px] w-16 bg-gradient-to-r from-crimson to-crimson-glow" data-id="element-96"></div>

                  <div className="flex items-center gap-3 mb-5" data-id="element-97">
                    <span className="w-1.5 h-1.5 bg-crimson-glow rounded-full animate-dot-pulse" data-id="element-98"></span>
                    <span className="font-sans text-[10px] tracking-[4px] uppercase text-gold-light font-medium" data-id="element-99">
                      {t('hero.form.label')}
                    </span>
                  </div>

                  <div className="space-y-3" data-id="element-100">
                    <div className="relative group" data-id="element-101">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 group-focus-within:text-crimson-glow transition-colors duration-300" strokeWidth={1.5} data-id="element-102" />
                      <input type="text" required value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} placeholder={t('hero.form.name')} className="w-full bg-white/[0.03] border border-white/[0.08] rounded-sm pl-11 pr-4 py-3.5 text-[14px] text-white placeholder-white/40 font-sans tracking-[0.02em] focus:outline-none focus:border-crimson/60 focus:bg-white/[0.05] transition-all duration-300" data-id="element-103" />
                    </div>

                    <div className="relative group" data-id="element-104">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 group-focus-within:text-crimson-glow transition-colors duration-300" strokeWidth={1.5} data-id="element-105" />
                      <input type="tel" required value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} placeholder={t('hero.form.phone')} className="w-full bg-white/[0.03] border border-white/[0.08] rounded-sm pl-11 pr-4 py-3.5 text-[14px] text-white placeholder-white/40 font-sans tracking-[0.02em] focus:outline-none focus:border-crimson/60 focus:bg-white/[0.05] transition-all duration-300" data-id="element-106" />
                    </div>

                    <div className="relative group" data-id="element-107">
                      <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 group-focus-within:text-crimson-glow transition-colors duration-300 pointer-events-none" strokeWidth={1.5} data-id="element-108" />
                      <select required value={formData.caseType} onChange={e => setFormData({
                    ...formData,
                    caseType: e.target.value
                  })} className="w-full bg-white/[0.03] border border-white/[0.08] rounded-sm pl-11 pr-10 py-3.5 text-[14px] text-white font-sans tracking-[0.02em] focus:outline-none focus:border-crimson/60 focus:bg-white/[0.05] transition-all duration-300 appearance-none cursor-pointer [&>option]:bg-black [&>option]:text-white" data-id="element-109">
                        <option value="" disabled data-id="element-110">
                          {t('hero.form.case.placeholder')}
                        </option>
                        <option value="auto" data-id="element-111">{t('hero.form.case.auto')}</option>
                        <option value="truck" data-id="element-112">
                          {t('hero.form.case.truck')}
                        </option>
                        <option value="slip" data-id="element-113">{t('hero.form.case.slip')}</option>
                        <option value="motorcycle" data-id="element-114">
                          {t('hero.form.case.motorcycle')}
                        </option>
                        <option value="other" data-id="element-115">
                          {t('hero.form.case.other')}
                        </option>
                      </select>
                      <ArrowRight className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 rotate-90 pointer-events-none" strokeWidth={1.5} data-id="element-116" />
                    </div>
                  </div>

                  {/* HIGH-ENERGY SUBMIT BUTTON */}
                  <div className="relative mt-6" data-id="element-117">
                    {/* Outer pulsing rings — 2 layers staggered for richer energy */}
                    {!submitting && <>
                        <motion.span aria-hidden="true" className="absolute inset-0 rounded-sm bg-crimson/40 pointer-events-none" animate={{
                    scale: [1, 1.08, 1.16],
                    opacity: [0.5, 0.2, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeOut'
                  }} data-id="element-118" />
                        <motion.span aria-hidden="true" className="absolute inset-0 rounded-sm bg-crimson/30 pointer-events-none" animate={{
                    scale: [1, 1.08, 1.16],
                    opacity: [0.5, 0.2, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeOut',
                    delay: 1
                  }} data-id="element-119" />
                      </>}

                    <motion.button type="submit" disabled={!isValid || submitting} animate={!submitting ? {
                  y: [0, -2, 0],
                  boxShadow: ['0 15px 50px rgba(153,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1) inset', '0 20px 70px rgba(220,38,38,0.75), 0 0 30px rgba(220,38,38,0.4), 0 0 0 1px rgba(255,255,255,0.25) inset', '0 15px 50px rgba(153,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1) inset']
                } : {}} whileHover={isValid && !submitting ? {
                  y: -5,
                  scale: 1.015
                } : {}} whileTap={isValid && !submitting ? {
                  scale: 0.96
                } : {}} transition={{
                  y: {
                    duration: 2.4,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  },
                  boxShadow: {
                    duration: 2.4,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  },
                  scale: {
                    type: 'spring',
                    stiffness: 400,
                    damping: 20
                  }
                }} className="relative w-full bg-gradient-to-r from-crimson via-crimson-glow to-crimson bg-[length:200%_100%] text-white px-8 py-5 text-[12px] tracking-[5px] uppercase font-bold rounded-sm inline-flex items-center justify-center gap-4 overflow-hidden disabled:cursor-not-allowed transition-all duration-500 group" data-id="element-120">
                      {/* Rotating conic border glow */}
                      {!submitting && <motion.span aria-hidden="true" className="absolute -inset-[2px] rounded-sm pointer-events-none opacity-70" style={{
                    background: 'conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.8) 40deg, transparent 100deg, transparent 180deg, rgba(255,200,200,0.5) 220deg, transparent 280deg, transparent 360deg)'
                  }} animate={{
                    rotate: [0, 360]
                  }} transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: 'linear'
                  }} data-id="element-121" />}
                      {/* Inner mask to keep only border visible */}
                      {!submitting && <span aria-hidden="true" className="absolute inset-0 rounded-sm bg-gradient-to-r from-crimson via-crimson-glow to-crimson pointer-events-none" data-id="element-122" />}

                      {/* Floating spark particles rising from bottom */}
                      {!submitting && <>
                          {[0, 1, 2, 3].map(i => <motion.span key={`spark-${i}`} aria-hidden="true" className="absolute w-1 h-1 rounded-full bg-white pointer-events-none z-[5]" style={{
                      left: `${15 + i * 22}%`,
                      bottom: '15%',
                      boxShadow: '0 0 8px rgba(255,255,255,1), 0 0 16px rgba(255,200,200,0.6)'
                    }} animate={{
                      y: [0, -30, -50],
                      opacity: [0, 1, 0],
                      scale: [0.4, 1.2, 0.3]
                    }} transition={{
                      duration: 1.8,
                      repeat: Infinity,
                      delay: i * 0.45,
                      ease: 'easeOut'
                    }} data-id="element-123" />)}
                        </>}

                      {/* Animated gradient shift background */}
                      {!submitting && <motion.span aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-crimson via-crimson-glow to-crimson bg-[length:200%_100%] pointer-events-none" animate={{
                    backgroundPosition: ['0% 0%', '200% 0%']
                  }} transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'linear'
                  }} data-id="element-124" />}

                      {/* Top highlight bar */}
                      <span aria-hidden="true" className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent pointer-events-none" data-id="element-125" />

                      {/* Bottom shadow line */}
                      <span aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-black/40 to-transparent pointer-events-none" data-id="element-126" />

                      {/* Animated shine sweep — faster + more frequent */}
                      {!submitting && <motion.span aria-hidden="true" className="absolute inset-y-0 -left-1/2 w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-20deg] pointer-events-none" animate={{
                    left: ['-50%', '150%']
                  }} transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    repeatDelay: 1.2,
                    ease: 'easeInOut'
                  }} data-id="element-127" />}

                      {/* Lightning bolt icon — pulses with energy */}
                      {!submitting && <motion.span aria-hidden="true" className="relative z-10 inline-flex" animate={{
                    scale: [1, 1.2, 1],
                    rotate: [0, -8, 0]
                  }} transition={{
                    duration: 1.4,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} data-id="element-128">
                          <Zap className="w-4 h-4 fill-white text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" strokeWidth={2} data-id="element-129" />
                        </motion.span>}

                      {/* Submitting spinner */}
                      {submitting && <motion.span className="relative z-10 inline-flex" animate={{
                    rotate: 360
                  }} transition={{
                    duration: 0.8,
                    repeat: Infinity,
                    ease: 'linear'
                  }} data-id="element-130">
                          <Loader2 className="w-4 h-4" strokeWidth={2.5} data-id="element-131" />
                        </motion.span>}

                      <span className="relative z-10 font-sans drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]" data-id="element-132">
                        {submitting ? t('hero.form.submitting') : t('hero.form.submit')}
                      </span>

                      {/* Arrow with stronger nudge */}
                      {!submitting && <motion.span className="relative z-10 inline-flex" animate={{
                    x: [0, 6, 0]
                  }} transition={{
                    duration: 1.4,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} data-id="element-133">
                          <ArrowRight className="w-5 h-5 drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]" strokeWidth={2} data-id="element-134" />
                        </motion.span>}
                    </motion.button>
                  </div>

                  <p className="mt-4 font-sans text-[10px] tracking-[2px] uppercase text-white/40 text-center" data-id="element-135">
                    {t('hero.form.disclaimer')}
                  </p>
                </motion.form> : <motion.div key="success" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative bg-black/40 backdrop-blur-2xl border border-crimson/30 rounded-sm p-8 lg:p-10 shadow-[0_30px_80px_rgba(122,0,0,0.3)]" data-id="element-136">
                  <div className="absolute top-0 left-0 h-[2px] w-full bg-gradient-to-r from-crimson via-crimson-glow to-crimson" data-id="element-137"></div>
                  <motion.div initial={{
                scale: 0,
                rotate: -45
              }} animate={{
                scale: 1,
                rotate: 0
              }} transition={{
                delay: 0.2,
                duration: 0.6,
                ease: [0.16, 1, 0.3, 1]
              }} className="mb-5" data-id="element-138">
                    <CheckCircle2 className="w-10 h-10 text-crimson-glow" strokeWidth={1.5} data-id="element-139" />
                  </motion.div>
                  <h3 className="font-serif text-[28px] lg:text-[32px] text-white mb-3 tracking-[-0.01em]" data-id="element-140">
                    {t('hero.form.success.title')}
                  </h3>
                  <p className="font-sans text-[14px] text-white/70 leading-[1.7]" data-id="element-141">
                    {t('hero.form.success.body')}
                  </p>
                </motion.div>}
            </AnimatePresence>

            <a href="tel:+13055550199" className="mt-5 inline-flex items-center gap-3 text-white/60 hover:text-white transition-colors duration-300 group" data-id="element-142">
              <Phone className="w-3.5 h-3.5 text-crimson-glow" strokeWidth={1.5} data-id="element-143" />
              <span className="font-sans text-[11px] tracking-[3px] uppercase border-b border-white/20 group-hover:border-crimson-glow transition-colors duration-300 pb-0.5" data-id="element-144">
                {t('hero.btn.secondary')}
              </span>
            </a>
          </div>
        </motion.div>
      </div>

      {/* Atmospheric effects moved above image so they enhance rather than hide it */}
      <div className="absolute top-0 right-0 w-[60%] h-full hero-rain animate-rain-fall z-[2] pointer-events-none mix-blend-screen opacity-60" data-id="element-145"></div>
      <div className="absolute right-[10%] top-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full hero-orb animate-orb-pulse z-[2] pointer-events-none mix-blend-screen opacity-70" data-id="element-146"></div>

      <div className="absolute bottom-0 left-0 w-full h-[200px] hero-bottom-fade z-20 pointer-events-none" data-id="element-147"></div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 0.3
    }} transition={{
      duration: 2,
      delay: 2.5
    }} className="absolute bottom-12 left-8 lg:left-[60px] z-30 flex items-center gap-4" data-id="element-148">
        <div className="w-12 h-[1px] bg-white/20 relative overflow-hidden" data-id="element-149">
          <div className="absolute top-0 left-[-100%] w-full h-full bg-white animate-scroll-move" data-id="element-150"></div>
        </div>
        <span className="font-sans text-[9px] tracking-[6px] uppercase text-white/80" data-id="element-151">
          {t('hero.scroll')}
        </span>
      </motion.div>
    </section>;
}